import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { RIDE_ROUTE_SLUG, parseLang, rideThereTooSoon, rideTileFor, suggestedPickup } from "@majourneys/shared";
import { PW, pewterPanelApproved, pewterPrimary, transferVehicle, useTransferMoney, type RidePromptProps } from "@majourneys/booking";
import { transferRoutesQueryOptions, type TransferRouteDto } from "../api/transfers.api";
import { formatDay } from "../lib/ride-time";
import { TRANSPORT_CITY } from "../lib/routes";

// The rules live in @majourneys/shared so the app's confirmation offers the
// same ride on the same bookings; re-exported for this page's callers.
export { rideTileFor, suggestedPickup, rideThereTooSoon };

/** The transport link, filled in with the booking's place, day and pick-up. */
export function rideThereHref(lang: string, p: RidePromptProps): string | null {
  const tile = rideTileFor(p);
  if (!tile) return null;
  const q = new URLSearchParams({ ride: tile, place: p.title.trim(), date: p.date.slice(0, 10) });
  const time = suggestedPickup(p.time, tile);
  if (time) q.set("time", time);
  return `/${lang}/transport?${q.toString()}`;
}

const TILE_ROUTE = RIDE_ROUTE_SLUG;

/** The fixed price of the smallest vehicle that seats the party. */
function priceFor(route: TransferRouteDto | undefined, people: number): number | null {
  if (!route) return null;
  const fitting = route.packages
    .filter((p) => p.maxGuests != null && p.maxGuests >= people)
    .sort((a, b) => (a.maxGuests ?? 0) - (b.maxGuests ?? 0));
  return fitting[0]?.price ?? null;
}

/**
 * "Add a private driver" on the thank-you page of a Marrakech restaurant or
 * club, or an Agafay evening or camp: the ride drawn from the guest's
 * accommodation to the place, with the day, the suggested pick-up, the car
 * for the party and its price. The button opens the transport card filled
 * in. Not for a closed or past booking, one whose pick-up is under two hours
 * away, nor one that bought the venue's own hotel pick-up.
 */
export function RideThereCard(p: RidePromptProps) {
  const { t, i18n } = useTranslation();
  const lang = parseLang((i18n.language || "en").split("-")[0]);
  const closed = p.status === "CANCELLED" || p.status === "REJECTED" || p.status === "COMPLETED";
  const href = rideThereHref(lang, p);
  const tile = rideTileFor(p);
  const show = !closed && !p.hasPickup && href != null && tile != null && !rideThereTooSoon(p);
  const { data: routes } = useQuery({ ...transferRoutesQueryOptions(TRANSPORT_CITY, lang), enabled: show });
  const money = useTransferMoney(lang);
  if (!show || !tile) return null;
  const people = Math.max(1, p.people || 1);
  const vehicle = transferVehicle(people <= 3 ? 3 : people <= 7 ? 7 : 17);
  const amount = priceFor(routes?.find((r) => r.slug === TILE_ROUTE[tile]), people);
  const pickup = suggestedPickup(p.time, tile);
  const day = formatDay(p.date.slice(0, 10), lang);
  return (
    <section className="p-4 md:p-5" style={pewterPanelApproved(22)} data-testid="ride-there">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-heading text-[16px] font-bold" style={{ color: PW.navy }}>
          {t("transport.ride_there_title")}
        </h2>
        <span className="shrink-0 rounded-full px-2.5 py-0.5 text-[11.5px] font-bold" style={{ background: PW.goldTint, color: PW.goldDark }}>
          {t("transport.ride_there_tag")}
        </span>
      </div>
      <div className="mt-3 flex items-stretch gap-3">
        <div className="flex flex-col items-center py-1" aria-hidden>
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: PW.navy }} />
          <span className="my-1 w-[2px] flex-1" style={{ background: PW.hairline }} />
          <Icon name="ri-map-pin-2-fill" className="text-[14px]" style={{ color: PW.navy }} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-between gap-2 text-[14px]" style={{ color: PW.navy }}>
          <div className="min-w-0">
            <div className="font-semibold">{t("transport.ride_there_from")}</div>
            <div className="text-[12.5px]" style={{ color: PW.muted }}>
              {pickup ? `${day} · ${pickup}` : day}
            </div>
          </div>
          <div className="line-clamp-2 break-words font-semibold">{p.title}</div>
        </div>
        <img src={vehicle.icon} alt={t(vehicle.nameKey)} width={64} height={48} className="h-12 w-16 shrink-0 self-center object-contain" />
      </div>
      <a
        href={href!}
        className="mt-4 flex min-h-[46px] w-full items-center justify-between gap-3 rounded-full px-5 text-[14.5px] font-semibold"
        style={pewterPrimary}
      >
        <span>{t("transport.ride_there_cta")}</span>
        {amount != null && <span>{money(amount)}</span>}
      </a>
      <p className="mt-2 text-center text-[12px]" style={{ color: PW.muted }}>
        {t("transport.ride_there_note")}
      </p>
    </section>
  );
}
